import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { getColor, getItemFromStore } from "helpers/utils";
import { Config, initialConfig } from "config";
import { ACTIONTYPE, configReducer, SET_CONFIG } from "reducers/ConfigReducer";
import { useTranslation } from "react-i18next";

interface AppContextInterface {
  config: Config;
  configDispatch: Dispatch<ACTIONTYPE>;
  toggleTheme: () => void;
  setConfig: (payload: Partial<Config>) => void;
  getThemeColor: (name: string) => string;
}

export const AppContext = createContext({} as AppContextInterface);

const AppProvider = ({ children }: PropsWithChildren) => {
  const { i18n } = useTranslation();

  const configState: Config = {
    isNavbarVerticalCollapsed: getItemFromStore(
      "isNavbarVerticalCollapsed",
      initialConfig.isNavbarVerticalCollapsed
    ),
    openNavbarVertical: initialConfig.openNavbarVertical,
    theme: getItemFromStore("theme", initialConfig.theme),
    navbarTopAppearance: getItemFromStore(
      "navbarTopAppearance",
      initialConfig.navbarTopAppearance
    ),
    navbarVerticalAppearance: getItemFromStore(
      "navbarVerticalAppearance",
      initialConfig.navbarVerticalAppearance
    ),
    navbarPosition: getItemFromStore(
      "navbarPosition",
      initialConfig.navbarPosition
    ),
    navbarTopShape: getItemFromStore(
      "navbarTopShape",
      initialConfig.navbarTopShape
    ),
    isRTL: getItemFromStore("isRTL", initialConfig.isRTL),
    isDark: getItemFromStore("isDark", initialConfig.isDark),
    isChatWidgetVisible: getItemFromStore(
      "isChatWidgetVisible",
      initialConfig.isChatWidgetVisible
    ),
  };

  const [config, configDispatch] = useReducer(configReducer, configState);

  const setConfig = (payload: Partial<Config>) => {
    configDispatch({
      type: SET_CONFIG,
      payload,
    });
  };

  const toggleTheme = () => {
    configDispatch({
      type: SET_CONFIG,
      payload: {
        theme: config.isDark ? "light" : "dark",
      },
    });
  };

  const getThemeColor = (name: string) => getColor(name);

  // Handle RTL dynamically without re-render
  useEffect(() => {
    const isArabic = i18n.language === "ar";
    document.documentElement.setAttribute("dir", isArabic ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", i18n.language);
    setConfig({
      isRTL: isArabic,
    });
  }, [i18n.language]);

  useEffect(() => {
    if (config.navbarTopShape === "slim") {
      document.documentElement.setAttribute(
        "data-navbar-horizontal-shape",
        "slim"
      );
    } else {
      document.documentElement.removeAttribute("data-navbar-horizontal-shape");
    }

    if (config.navbarPosition === "dual") {
      setConfig({
        navbarTopShape: "default",
      });
    }

    document.documentElement.setAttribute(
      "data-navigation-type",
      config.navbarPosition
    );

    if (config.isNavbarVerticalCollapsed) {
      document.documentElement.classList.add("navbar-vertical-collapsed");
    } else {
      document.documentElement.classList.remove("navbar-vertical-collapsed");
    }
  }, [config]);

  return (
    <AppContext.Provider
      value={{ config, setConfig, toggleTheme, getThemeColor, configDispatch }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
